// ─────────────────────────────────────────────────────────────────────────────
// D.28d — Date recurrence calculation helper.
//
// EXACT PORT of backend calculateMonthlyDates from
// /var/www/safecore/backend/src/controllers/recurringController.js.
//
// Both frontend and backend MUST produce identical date sets for the same
// inputs. Any change here REQUIRES the backend twin to be updated in lockstep,
// or the preview will lie about what's actually scheduled.
//
// Returns: [{ date: 'YYYY-MM-DD', is_extra: bool }]
// ─────────────────────────────────────────────────────────────────────────────

// NO-UTC: format date as YYYY-MM-DD using local components only.
function fmtYMD(d) {
  return d.getFullYear() + '-' +
    String(d.getMonth() + 1).padStart(2, '0') + '-' +
    String(d.getDate()).padStart(2, '0');
}

export function calculateMonthDates(pattern_code, config, year, month, startDate = null) {
  const results = [];
  const firstDay = new Date(year, month, 1);
  const lastDay  = new Date(year, month + 1, 0);

  const inEffectiveRange = (d) => !startDate || fmtYMD(d) >= startDate;

  if (pattern_code === 'weekly') {
    const dayOfWeek = config.day_of_week;
    let count = 0;
    for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
      if (d.getDay() === dayOfWeek && inEffectiveRange(d)) {
        count++;
        results.push({ date: new Date(d), is_extra: count > 4 });
      }
    }

  } else if (pattern_code === '2x_week') {
    const days = config.days_of_week || [];
    let count = 0;
    for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
      if (days.includes(d.getDay()) && inEffectiveRange(d)) {
        count++;
        results.push({ date: new Date(d), is_extra: count > 8 });
      }
    }

  } else if (pattern_code === '3x_week') {
    const days = config.days_of_week || [];
    let count = 0;
    for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
      if (days.includes(d.getDay()) && inEffectiveRange(d)) {
        count++;
        results.push({ date: new Date(d), is_extra: count > 12 });
      }
    }

  } else if (pattern_code === 'daily') {
    const cfgDays = Array.isArray(config.days_of_week) && config.days_of_week.length > 0
      ? config.days_of_week.map(n => n % 7)
      : [1, 2, 3, 4, 5];
    const standardCount = cfgDays.length * 4;
    let count = 0;
    for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
      if (cfgDays.includes(d.getDay()) && inEffectiveRange(d)) {
        count++;
        results.push({ date: new Date(d), is_extra: count > standardCount });
      }
    }

  } else if (pattern_code === 'bimonthly') {
    const dayOfMonth = parseInt(config.day_of_month, 10) || 1;
    if (startDate) {
      const startParts = startDate.split('-').map(Number);
      const startMonthAbs = startParts[0] * 12 + (startParts[1] - 1);
      const currentMonthAbs = year * 12 + month;
      const monthsSinceStart = currentMonthAbs - startMonthAbs;
      if (monthsSinceStart >= 0 && monthsSinceStart % 2 === 0) {
        const lastDayNum = new Date(year, month + 1, 0).getDate();
        const targetDay = Math.min(dayOfMonth, lastDayNum);
        const target = new Date(year, month, targetDay);
        if (inEffectiveRange(target)) {
          results.push({ date: target, is_extra: false });
        }
      }
    }

  } else if (pattern_code === 'quarterly') {
    const dayOfMonth = parseInt(config.day_of_month, 10) || 1;
    if (startDate) {
      const startParts = startDate.split('-').map(Number);
      const startMonthAbs = startParts[0] * 12 + (startParts[1] - 1);
      const currentMonthAbs = year * 12 + month;
      const monthsSinceStart = currentMonthAbs - startMonthAbs;
      if (monthsSinceStart >= 0 && monthsSinceStart % 3 === 0) {
        const lastDayNum = new Date(year, month + 1, 0).getDate();
        const targetDay = Math.min(dayOfMonth, lastDayNum);
        const target = new Date(year, month, targetDay);
        if (inEffectiveRange(target)) {
          results.push({ date: target, is_extra: false });
        }
      }
    }

  } else if (pattern_code === 'semiannual') {
    const dayOfMonth = parseInt(config.day_of_month, 10) || 1;
    if (startDate) {
      const startParts = startDate.split('-').map(Number);
      const startMonthAbs = startParts[0] * 12 + (startParts[1] - 1);
      const currentMonthAbs = year * 12 + month;
      const monthsSinceStart = currentMonthAbs - startMonthAbs;
      if (monthsSinceStart >= 0 && monthsSinceStart % 6 === 0) {
        const lastDayNum = new Date(year, month + 1, 0).getDate();
        const targetDay = Math.min(dayOfMonth, lastDayNum);
        const target = new Date(year, month, targetDay);
        if (inEffectiveRange(target)) {
          results.push({ date: target, is_extra: false });
        }
      }
    }

  } else if (pattern_code === 'biweekly') {
    const weeks = config.weeks || [];
    const dayOfWeek = config.day_of_week;
    weeks.forEach(weekNum => {
      const targetDate = new Date(year, month, 1 + (weekNum - 1) * 7);
      while (targetDate.getDay() !== dayOfWeek && targetDate.getMonth() === month) {
        targetDate.setDate(targetDate.getDate() + 1);
      }
      if (targetDate.getMonth() === month) {
        results.push({ date: new Date(targetDate), is_extra: false });
      }
    });

  } else if (pattern_code === 'monthly') {
    // D.28d-1.5: filter inEffectiveRange so pre-start dates aren't generated.
    // Lockstep with backend recurringController.calculateMonthlyDates.
    const weekOfMonth = config.week_of_month;
    const dayOfWeek = config.day_of_week;
    if (weekOfMonth === -1) {
      for (let d = new Date(lastDay); d >= firstDay; d.setDate(d.getDate() - 1)) {
        if (d.getDay() === dayOfWeek) {
          if (inEffectiveRange(d)) {
            results.push({ date: new Date(d), is_extra: false });
          }
          break;
        }
      }
    } else {
      const targetDate = new Date(year, month, 1 + (weekOfMonth - 1) * 7);
      while (targetDate.getDay() !== dayOfWeek && targetDate.getMonth() === month) {
        targetDate.setDate(targetDate.getDate() + 1);
      }
      if (targetDate.getMonth() === month && inEffectiveRange(targetDate)) {
        results.push({ date: new Date(targetDate), is_extra: false });
      }
    }
  }

  return results.map(r => ({
    date:     fmtYMD(r.date),
    is_extra: r.is_extra
  }));
}

// Format YYYY-MM-DD string for display. Parses as T12:00:00 to avoid midnight UTC shift.
export function formatDateForDisplay(ymdString) {
  const d = new Date(ymdString + 'T12:00:00');
  return d.toLocaleDateString('en-US', {
    weekday: 'short', year: 'numeric', month: 'short', day: 'numeric'
  });
}
